import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ArrowLeft, Filter, Sparkles, Eye, Send, Share2, Play, LayoutGrid } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { SavedVaultStats } from '@/components/saved/SavedVaultStats';
import { SavedMessageCard } from '@/components/saved/SavedMessageCard';
import { SavedEmptyState } from '@/components/saved/SavedEmptyState';
import { SavedDesktopSidebar } from '@/components/saved/SavedDesktopSidebar';
import { RevisitMode } from '@/components/saved/RevisitMode';
import { IdentityStreak } from '@/components/saved/IdentityStreak';
import { saveTryDraft } from '@/lib/tryDraft';
import { WIZARD_PREFS_KEY } from '@/components/wizard/WhoIsThisForStep';

const copy = {
  en: { title: 'My Life Vault', subtitle: 'The moments that stayed with you.', unsaved: 'Removed from saved', footerText: 'You felt something reading this…\nsend that feeling to someone else.', footerCta: 'Send a moment →', revisit: 'Revisit', grid: 'Grid', postShareTitle: 'This moment could mean everything to someone else.', postShareCta: 'Send one now →' },
  es: { title: 'Mi Bóveda de Vida', subtitle: 'Los momentos que se quedaron contigo.', unsaved: 'Eliminado de guardados', footerText: 'Sentiste algo al leer esto…\nenvía ese sentimiento a alguien más.', footerCta: 'Envía un momento →', revisit: 'Revivir', grid: 'Cuadrícula', postShareTitle: 'Este momento podría significar todo para alguien más.', postShareCta: 'Envía uno ahora →' },
  fr: { title: 'Mon Coffre de Vie', subtitle: 'Les moments qui sont restés avec vous.', unsaved: 'Retiré des sauvegardés', footerText: 'Vous avez ressenti quelque chose…\nenvoyez ce sentiment à quelqu\'un d\'autre.', footerCta: 'Envoyer un moment →', revisit: 'Revisiter', grid: 'Grille', postShareTitle: 'Ce moment pourrait tout signifier pour quelqu\'un d\'autre.', postShareCta: 'Envoyez-en un maintenant →' },
  pt: { title: 'Meu Cofre de Vida', subtitle: 'Os momentos que ficaram com você.', unsaved: 'Removido dos salvos', footerText: 'Você sentiu algo ao ler…\nenvie esse sentimento a outra pessoa.', footerCta: 'Envie um momento →', revisit: 'Revisitar', grid: 'Grade', postShareTitle: 'Este momento pode significar tudo para outra pessoa.', postShareCta: 'Envie um agora →' },
  ar: { title: 'خزنة حياتي', subtitle: 'اللحظات التي بقيت معك.', unsaved: 'تمت الإزالة من المحفوظات', footerText: 'شعرت بشيء عند قراءة هذا…\nأرسل هذا الشعور لشخص آخر.', footerCta: 'أرسل لحظة ←', revisit: 'إعادة الزيارة', grid: 'شبكة', postShareTitle: 'هذه اللحظة قد تعني كل شيء لشخص آخر.', postShareCta: 'أرسل واحدة الآن ←' },
  ja: { title: '私のライフ・ヴォールト', subtitle: 'あなたの心に残った瞬間。', unsaved: '保存から削除しました', footerText: 'あなたは何かを感じました…\nその気持ちを誰かに届けましょう。', footerCta: '想いを送る →', revisit: '再訪', grid: 'グリッド', postShareTitle: 'この瞬間は誰かにとってすべてかもしれません。', postShareCta: '今すぐ送る →' },
  ko: { title: '나의 라이프 볼트', subtitle: '마음에 남은 순간들.', unsaved: '저장에서 제거됨', footerText: '무언가를 느꼈다면…\n그 감정을 다른 누군가에게 전해보세요.', footerCta: '순간을 보내기 →', revisit: '다시 보기', grid: '그리드', postShareTitle: '이 순간은 다른 누군가에게 모든 것을 의미할 수 있습니다.', postShareCta: '지금 보내기 →' },
  id: { title: 'Brankas Hidup Saya', subtitle: 'Momen yang tetap bersama Anda.', unsaved: 'Dihapus dari tersimpan', footerText: 'Anda merasakan sesuatu saat membaca…\nkirim perasaan itu ke orang lain.', footerCta: 'Kirim momen →', revisit: 'Kunjungi Lagi', grid: 'Kisi', postShareTitle: 'Momen ini bisa berarti segalanya bagi orang lain.', postShareCta: 'Kirim sekarang →' },
  hi: { title: 'मेरी लाइफ वॉल्ट', subtitle: 'वो पल जो आपके साथ रहे।', unsaved: 'सहेजे गए से हटाया गया', footerText: 'आपने कुछ महसूस किया…\nवो एहसास किसी और को भेजें।', footerCta: 'एक पल भेजें →', revisit: 'फिर से देखें', grid: 'ग्रिड', postShareTitle: 'यह पल किसी और के लिए सब कुछ हो सकता है।', postShareCta: 'अभी भेजें →' },
};

export interface SavedItem {
  id: string;
  announcement_slug: string;
  created_at: string;
  announcement?: {
    recipient_name: string;
    message: string;
    template: string;
    animation_theme: string;
    photo_urls: string[] | null;
    sender_display_name: string | null;
    is_anonymous: boolean;
    relationship?: string;
  } | null;
}

export default function SavedMessages() {
  const { user, isHydrated } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const c = lc(copy, language);
  const [items, setItems] = useState<SavedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [revisitMode, setRevisitMode] = useState(false);
  const [showPostSharePrompt, setShowPostSharePrompt] = useState(false);

  useEffect(() => {
    if (!isHydrated || !user) return;
    (async () => {
      setLoading(true);
      try {
        const { data: saves, error: savesErr } = await (supabase as any)
          .from('saved_announcements')
          .select('id, announcement_slug, created_at')
          .eq('saver_identifier', user.id)
          .order('created_at', { ascending: false });

        if (savesErr || !saves?.length) { setItems([]); return; }

        const slugs = saves.map((s: any) => s.announcement_slug).filter(Boolean);
        const announcements = await Promise.all(slugs.map(async (slug: string) => {
          const { data } = await (supabase as any).rpc('get_announcement_by_slug', { p_slug: slug });
          const row = Array.isArray(data) ? data[0] : data;
          return row ? { ...row, public_slug: slug } : null;
        }));

        const map = new Map<string, any>();
        announcements.filter(Boolean).forEach((a: any) => map.set(a.public_slug, a));

        setItems(saves.map((s: any) => ({ ...s, announcement: map.get(s.announcement_slug) || null })));
      } catch { setItems([]); }
      finally { setLoading(false); }
    })();
  }, [isHydrated, user]);

  const handleUnsave = useCallback(async (e: React.MouseEvent, saveId: string) => {
    e.stopPropagation();
    e.preventDefault();
    const { error } = await (supabase as any).from('saved_announcements').delete().eq('id', saveId);
    if (error) return;
    setItems(prev => prev.filter(i => i.id !== saveId));
    toast.success(c.unsaved);
  }, [c.unsaved]);

  const handleSendAgain = useCallback((item: SavedItem) => {
    if (item.announcement) {
      saveTryDraft({
        recipientName: item.announcement.recipient_name || '',
        message: item.announcement.message || '',
      });
      try {
        sessionStorage.setItem(WIZARD_PREFS_KEY, JSON.stringify({
          relationship: item.announcement.relationship,
          template: item.announcement.template,
          animationTheme: item.announcement.animation_theme,
        }));
      } catch { /* noop */ }
    }
    navigate('/create');
  }, [navigate]);

  const handleShare = useCallback(async (item: SavedItem) => {
    const url = `${window.location.origin}/a/${item.announcement_slug}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Say It NOW', text: 'This meant something to me.', url });
      } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied!');
    }
    // Show post-share conversion prompt
    setShowPostSharePrompt(true);
    setTimeout(() => setShowPostSharePrompt(false), 6000);
  }, []);

  const lastSavedDate = useMemo(() => {
    if (!items.length) return null;
    return items[0].created_at;
  }, [items]);

  const filteredItems = useMemo(() => {
    if (!selectedFilter) return items;
    return items.filter(i => detectCategory(i) === selectedFilter);
  }, [items, selectedFilter]);

  return (
    <div className="min-h-screen bg-[hsl(220,25%,8%)] relative overflow-hidden">
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-950/30 via-transparent to-indigo-950/20" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-rose-600/6 rounded-full blur-[100px]" />
        <div className="absolute top-2/3 left-1/2 w-72 h-72 bg-amber-500/5 rounded-full blur-[100px]" />
      </div>

      {/* Revisit Mode overlay */}
      <AnimatePresence>
        {revisitMode && filteredItems.length > 0 && (
          <RevisitMode
            items={filteredItems}
            language={language}
            onClose={() => setRevisitMode(false)}
            onSendAgain={(item) => { setRevisitMode(false); handleSendAgain(item); }}
            onShare={handleShare}
          />
        )}
      </AnimatePresence>

      {/* Post-share conversion prompt */}
      <AnimatePresence>
        {showPostSharePrompt && (
          <motion.div
            className="fixed top-20 left-4 right-4 z-[80] lg:left-auto lg:right-8 lg:max-w-sm"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="bg-[hsl(220,25%,12%)] border border-white/10 rounded-2xl p-4 shadow-xl">
              <p className="text-[13px] text-white/70 mb-2 italic">{c.postShareTitle}</p>
              <Link to="/create" onClick={() => setShowPostSharePrompt(false)}>
                <Button size="sm" className="w-full bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-full font-semibold active:scale-[0.96] transition-transform">
                  {c.postShareCta}
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop layout with sidebar */}
      <div className="relative z-10 flex">
        <SavedDesktopSidebar
          totalSaved={items.length}
          lastSavedDate={lastSavedDate}
          selectedFilter={selectedFilter}
          onFilterChange={setSelectedFilter}
          items={items}
        />

        <div className="flex-1 lg:ml-[280px]">
          {/* Sticky header - mobile */}
          <header className="sticky top-0 z-50 h-[72px] flex items-center px-4 backdrop-blur-xl bg-[hsl(220,25%,8%)]/70 border-b border-white/5 lg:hidden">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-white/70 hover:text-white transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="flex-1 text-center text-lg font-bold text-white tracking-tight">
              {c.title}
            </h1>
            {/* Revisit mode toggle */}
            {items.length > 0 && (
              <button
                onClick={() => setRevisitMode(!revisitMode)}
                className={cn("p-2 -mr-2 transition-colors", revisitMode ? "text-primary" : "text-white/40")}
              >
                <Play className="h-5 w-5" />
              </button>
            )}
          </header>

          <main className="pb-40 lg:pb-32 lg:max-w-[960px] lg:mx-auto">
            {/* Desktop header */}
            <div className="hidden lg:flex lg:items-center lg:justify-between pt-10 pb-2 px-6">
              <div>
                <h1 className="text-3xl font-bold text-white tracking-tight">{c.title}</h1>
                <p className="text-sm text-white/70 mt-1 italic">{c.subtitle}</p>
              </div>
              {items.length > 0 && (
                <div className="flex items-center gap-1 bg-white/5 rounded-full p-1 border border-white/10">
                  <button
                    onClick={() => setRevisitMode(false)}
                    className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition-all",
                      !revisitMode ? "bg-white/10 text-white" : "text-white/70 hover:text-white"
                    )}
                  >
                    <LayoutGrid className="h-3.5 w-3.5" /> {c.grid}
                  </button>
                  <button
                    onClick={() => setRevisitMode(true)}
                    className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition-all",
                      revisitMode ? "bg-white/10 text-white" : "text-white/70 hover:text-white"
                    )}
                  >
                    <Play className="h-3.5 w-3.5" /> {c.revisit}
                  </button>
                </div>
              )}
            </div>

            {/* Stats + Identity streak - mobile */}
            {!loading && items.length > 0 && (
              <div className="lg:hidden">
                <SavedVaultStats
                  totalSaved={items.length}
                  lastSavedDate={lastSavedDate}
                  language={language}
                />
              </div>
            )}

            {/* Identity streak */}
            {!loading && items.length >= 2 && (
              <IdentityStreak totalSaved={items.length} language={language} />
            )}

            {/* Subtitle - mobile */}
            {!loading && items.length > 0 && (
              <p className="px-5 pb-4 text-sm text-white/70 italic lg:hidden">
                {c.subtitle}
              </p>
            )}

            {/* Filter pills - mobile */}
            {!loading && items.length > 2 && (
              <div className="px-4 pb-4 flex gap-2 overflow-x-auto scrollbar-hide lg:px-6">
                {(['all', 'love', 'gratitude', 'closure', 'celebration'] as const).map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedFilter(cat === 'all' ? null : cat)}
                    className={cn(
                      "shrink-0 px-3 py-1.5 rounded-full text-[12px] font-medium transition-all border",
                      (cat === 'all' && !selectedFilter) || selectedFilter === cat
                        ? "bg-white/15 border-white/25 text-white"
                        : "bg-white/5 border-white/10 text-white/40 hover:text-white/60"
                    )}
                  >
                    {cat === 'all' ? 'All' : `${CATEGORY_ICONS[cat]} ${cat.charAt(0).toUpperCase() + cat.slice(1)}`}
                  </button>
                ))}
              </div>
            )}

            {loading ? (
              <div className="px-4 lg:px-6 space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-[180px] rounded-[20px] bg-white/5 animate-pulse" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <SavedEmptyState language={language} />
            ) : (
              <div className="px-4 lg:px-6 space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-5">
                <AnimatePresence>
                  {filteredItems.map((item, idx) => (
                    <SavedMessageCard
                      key={item.id}
                      item={item}
                      index={idx}
                      language={language}
                      onUnsave={handleUnsave}
                      onSendAgain={handleSendAgain}
                      onShare={handleShare}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </main>

          {/* Sticky footer CTA */}
          {!loading && items.length > 0 && (
            <div className="fixed bottom-0 inset-x-0 lg:left-[280px] z-40 pb-6 pt-12 px-4 bg-gradient-to-t from-[hsl(220,25%,8%)] via-[hsl(220,25%,8%)]/95 to-transparent">
              <p className="text-center text-[13px] text-white/70 italic mb-3 whitespace-pre-line leading-relaxed">
                {c.footerText}
              </p>
              <Link to="/create" className="block max-w-md mx-auto">
                <Button className="w-full bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-full h-12 font-semibold shadow-lg shadow-primary/20 active:scale-[0.96] transition-transform">
                  {c.footerCta}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Category detection
const CATEGORY_KEYWORDS: Record<string, string[]> = {
  love: ['love', 'heart', 'forever', 'adore', 'cherish', 'darling', 'amor'],
  gratitude: ['thank', 'grateful', 'gratitude', 'appreciate', 'blessing', 'gracias'],
  closure: ['sorry', 'forgive', 'goodbye', 'peace', 'closure', 'regret', 'miss'],
  celebration: ['congrat', 'celebrate', 'proud', 'happy birthday', 'achievement', 'felicidades'],
};

const CATEGORY_ICONS: Record<string, string> = {
  love: '❤️',
  gratitude: '🙏',
  closure: '🕊',
  celebration: '🎉',
};

export function detectCategory(item: SavedItem): string {
  if (!item.announcement) return 'love';
  const template = item.announcement.template;
  if (template === 'forever' || template === 'warmth') return 'love';
  if (template === 'gratitude') return 'gratitude';
  if (template === 'courage') return 'closure';
  if (template === 'celebration') return 'celebration';

  const msg = item.announcement.message.toLowerCase();
  for (const [cat, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
    if (keywords.some(kw => msg.includes(kw))) return cat;
  }
  return 'love';
}

export { CATEGORY_ICONS };
